import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import AdminNavigation from "@/components/AdminNavigation";
import { 
  HelpCircle, 
  BookOpen, 
  Zap, 
  Settings, 
  Mail,
  CheckCircle,
  Loader2,
  FileText,
  Users,
  MessageSquare,
  AlertCircle
} from "lucide-react";
import { apiRequest } from "@/lib/queryClient";
import type { AdminDocs } from "@shared/schema";

export default function AdminSupport() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState("overview");

  // Fetch admin documentation
  const { data: docs, isLoading, error } = useQuery<AdminDocs>({
    queryKey: ['/api/admin/docs'],
    retry: 1
  });

  // Email documentation mutation
  const emailDocsMutation = useMutation({
    mutationFn: () => apiRequest('POST', '/api/admin/docs/email', {
      recipients: ["Craig.Ross@neuralquantum.ai", "Tommy.Xaypanya@neuralquantum.ai"]
    }),
    onSuccess: (data: any) => {
      toast({
        title: "Documentation Sent",
        description: `Successfully sent documentation to ${data.recipients?.length || 2} recipient(s)`,
      });
    },
    onError: (error: any) => {
      console.error("Email error:", error);
      toast({
        title: "Email Failed",
        description: error.message || "Failed to send documentation email",
        variant: "destructive",
      });
    }
  });

  const renderMarkdownContent = (content: string) => {
    if (!content) return '';
    
    // Simple HTML escaping for security
    const escapeHtml = (text: string) => {
      const div = document.createElement('div');
      div.textContent = text;
      return div.innerHTML;
    };
    
    // Process inline formatting safely by escaping everything then applying controlled formatting
    const processInlineFormatting = (text: string) => {
      // First escape ALL HTML to prevent any XSS
      let escaped = escapeHtml(text);
      
      // Now apply safe formatting on the escaped text using placeholders
      const placeholders: { [key: string]: string } = {};
      let placeholderIndex = 0;
      
      // Helper to create unique placeholder
      const createPlaceholder = (html: string) => {
        const placeholder = `__PLACEHOLDER_${placeholderIndex++}__`;
        placeholders[placeholder] = html;
        return placeholder;
      };
      
      // Process bold text on escaped content
      escaped = escaped.replace(/\*\*(.*?)\*\*/g, (match, content) => {
        const html = `<strong class="font-semibold text-gray-900">${content}</strong>`;
        return createPlaceholder(html);
      });
      
      // Process italic text (check for conflicts with bold manually)
      escaped = escaped.replace(/\*([^*]+)\*/g, (match, content, offset, string) => {
        // Check if this is part of a bold pattern by looking at surrounding context
        const beforeMatch = string.slice(Math.max(0, offset - 1), offset + 1);
        const afterMatch = string.slice(offset + match.length - 1, offset + match.length + 1);
        
        if (beforeMatch.includes('**') || afterMatch.includes('**')) {
          return match; // Skip if part of bold
        }
        
        const html = `<em class="italic text-gray-700">${content}</em>`;
        return createPlaceholder(html);
      });
      
      // Process inline code
      escaped = escaped.replace(/`([^`]+)`/g, (match, code) => {
        const html = `<code class="bg-gray-100 px-1 py-0.5 rounded text-sm font-mono">${code}</code>`;
        return createPlaceholder(html);
      });
      
      // Process links
      escaped = escaped.replace(/\[([^\]]+)\]\(([^)]+)\)/g, (match, linkText, url) => {
        try {
          let validUrl;
          if (url.startsWith('mailto:')) {
            validUrl = new URL(url);
          } else if (url.startsWith('http://') || url.startsWith('https://')) {
            validUrl = new URL(url);
          } else if (/^[\w\.-]+\.[a-z]{2,}/.test(url)) {
            validUrl = new URL(`https://${url}`);
          } else {
            return match; // Keep escaped version
          }
          
          if (!['http:', 'https:', 'mailto:'].includes(validUrl.protocol)) {
            return match; // Keep escaped version
          }
          
          const target = validUrl.protocol === 'mailto:' ? '' : ' target="_blank" rel="noopener noreferrer"';
          const html = `<a href="${escapeHtml(validUrl.href)}" class="text-blue-600 hover:text-blue-800 underline"${target}>${linkText}</a>`;
          return createPlaceholder(html);
        } catch {
          return match; // Keep escaped version
        }
      });
      
      // Process email addresses (avoid creating nested links)
      escaped = escaped.replace(/(^|[^&])([\w\.-]+@[\w\.-]+\.\w+)/g, (match, prefix, email) => {
        if (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
          const html = `${prefix}<a href="mailto:${email}" class="text-purple-600 hover:text-purple-800 font-medium">${email}</a>`;
          return createPlaceholder(html);
        }
        return match;
      });
      
      // Replace placeholders with actual HTML
      for (const [placeholder, html] of Object.entries(placeholders)) {
        escaped = escaped.replace(placeholder, html);
      }
      
      return escaped;
    };
    
    // Split content into blocks first to handle different content types
    const blocks = content.split(/\n\s*\n/);
    
    const processedBlocks = blocks.map(block => {
      if (block.trim() === '') return '';
      
      // Check if this is a header
      if (/^#{1,4}\s/.test(block.trim())) {
        const lines = block.split('\n');
        const processedLines = lines.map(line => {
          const trimmed = line.trim();
          if (/^# (.*)$/.test(trimmed)) {
            const title = trimmed.replace(/^# /, '');
            return `<h1 class="text-2xl font-bold mb-4 text-purple-600">${escapeHtml(title)}</h1>`;
          } else if (/^## (.*)$/.test(trimmed)) {
            const title = trimmed.replace(/^## /, '');
            return `<h2 class="text-xl font-semibold mb-3 text-purple-600">${escapeHtml(title)}</h2>`;
          } else if (/^### (.*)$/.test(trimmed)) {
            const title = trimmed.replace(/^### /, '');
            return `<h3 class="text-lg font-medium mb-2 text-purple-600">${escapeHtml(title)}</h3>`;
          } else if (/^#### (.*)$/.test(trimmed)) {
            const title = trimmed.replace(/^#### /, '');
            return `<h4 class="text-base font-medium mb-2 text-gray-700">${escapeHtml(title)}</h4>`;
          } else {
            // Non-header line - process as inline content
            return processInlineFormatting(line);
          }
        });
        return processedLines.join('\n');
      }
      
      // Check if this is a code block
      if (/^```/.test(block.trim())) {
        const lines = block.split('\n');
        let inCodeBlock = false;
        let codeContent = [];
        const processedLines = [];
        
        for (const line of lines) {
          if (line.trim().startsWith('```')) {
            if (inCodeBlock) {
              // End of code block
              const code = codeContent.join('\n');
              processedLines.push(`<pre class="bg-gray-100 p-3 rounded-md my-2 overflow-x-auto"><code class="text-sm">${escapeHtml(code)}</code></pre>`);
              codeContent = [];
              inCodeBlock = false;
            } else {
              // Start of code block
              inCodeBlock = true;
            }
          } else if (inCodeBlock) {
            codeContent.push(line);
          } else {
            // Non-code line - process as inline content
            processedLines.push(processInlineFormatting(line));
          }
        }
        
        return processedLines.join('\n');
      }
      
      // Check if this is a list block (fixed regex with proper anchoring)
      if (/^\s*(?:[-*]|\d+\.)\s/.test(block.trim())) {
        const lines = block.split('\n');
        let listItems = [];
        let listType = '';
        
        for (const line of lines) {
          const trimmed = line.trim();
          if (/^\s*[-*]\s/.test(trimmed)) {
            // Process inline formatting before escaping
            const text = trimmed.replace(/^\s*[-*]\s/, '');
            const formatted = processInlineFormatting(text);
            listItems.push(`<li class="ml-6 mb-1 list-disc">${formatted}</li>`);
            listType = 'disc';
          } else if (/^\s*\d+\.\s/.test(trimmed)) {
            // Process inline formatting before escaping
            const text = trimmed.replace(/^\s*\d+\.\s/, '');
            const formatted = processInlineFormatting(text);
            listItems.push(`<li class="ml-6 mb-1 list-decimal">${formatted}</li>`);
            listType = 'decimal';
          }
        }
        
        // Safety check: if no list items were created, treat as paragraph
        if (listItems.length === 0) {
          const formatted = processInlineFormatting(block);
          const withBreaks = formatted.replace(/\n/g, '<br>');
          return `<p class="mb-3 text-gray-700 leading-relaxed">${withBreaks}</p>`;
        }
        
        // Wrap in appropriate list tag
        const listContent = listItems.join('\n');
        if (listType === 'disc') {
          return `<ul class="mb-4">${listContent}</ul>`;
        } else {
          return `<ol class="mb-4">${listContent}</ol>`;
        }
      }
      
      // Regular paragraph - process inline formatting
      const formatted = processInlineFormatting(block);
      const withBreaks = formatted.replace(/\n/g, '<br>');
      return `<p class="mb-3 text-gray-700 leading-relaxed">${withBreaks}</p>`;
    });
    
    return processedBlocks.join('\n');
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <AdminNavigation currentPage="Support & Documentation" />
        <div className="max-w-7xl mx-auto p-4 md:p-6 space-y-6 md:space-y-8">
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-8 w-8 animate-spin text-purple-600" />
            <span className="ml-2 text-gray-600">Loading documentation...</span>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50">
        <AdminNavigation currentPage="Support & Documentation" />
        <div className="max-w-7xl mx-auto p-4 md:p-6 space-y-6 md:space-y-8">
          <Card className="border-red-200 bg-red-50" data-testid="error-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-red-600">
                <AlertCircle className="h-5 w-5" />
                Error Loading Documentation
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-red-600">Failed to load admin documentation. Please try refreshing the page.</p>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  if (!docs) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-50" data-testid="admin-support">
      <AdminNavigation currentPage="Support & Documentation" />
      
      <div className="max-w-7xl mx-auto p-4 md:p-6 space-y-6 md:space-y-8">
        {/* Page Header */}
        <div className="flex flex-col space-y-4 md:space-y-0 md:flex-row md:justify-between md:items-center">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-gray-900">
              Support & Documentation
            </h1>
            <p className="text-sm md:text-base text-gray-600 mt-1 md:mt-2">
              Comprehensive guides, FAQs, and support resources for admin operations
            </p>
          </div>
          <Button 
            onClick={() => emailDocsMutation.mutate()}
            disabled={emailDocsMutation.isPending}
            className="w-full md:w-auto bg-gradient-to-r from-purple-600 to-teal-600 hover:from-purple-700 hover:to-teal-700"
            data-testid="button-email-docs"
          >
            {emailDocsMutation.isPending ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                Sending...
              </>
            ) : (
              <>
                <Mail className="h-4 w-4 mr-2" />
                Email Documentation
              </>
            )}
          </Button>
        </div>

        {/* Documentation Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
          <TabsList className="grid w-full grid-cols-5 h-auto p-1" data-testid="tabs-list">
            <TabsTrigger value="overview" className="flex items-center gap-2" data-testid="tab-overview">
              <BookOpen className="h-4 w-4" />
              <span className="hidden md:block">Overview</span>
            </TabsTrigger>
            <TabsTrigger value="quickstarts" className="flex items-center gap-2" data-testid="tab-quickstarts">
              <Zap className="h-4 w-4" />
              <span className="hidden md:block">Quick Starts</span>
            </TabsTrigger>
            <TabsTrigger value="features" className="flex items-center gap-2" data-testid="tab-features">
              <Settings className="h-4 w-4" />
              <span className="hidden md:block">Features</span>
            </TabsTrigger>
            <TabsTrigger value="faq" className="flex items-center gap-2" data-testid="tab-faq">
              <HelpCircle className="h-4 w-4" />
              <span className="hidden md:block">FAQ</span>
            </TabsTrigger>
            <TabsTrigger value="support" className="flex items-center gap-2" data-testid="tab-support">
              <MessageSquare className="h-4 w-4" />
              <span className="hidden md:block">Support</span>
            </TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview" className="space-y-4" data-testid="tab-content-overview">
            <Card className="bg-white/70 backdrop-blur-sm border-0 shadow-lg">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 bg-gradient-to-r from-purple-600 to-teal-600 bg-clip-text text-transparent">
                  <BookOpen className="h-5 w-5 text-purple-600" />
                  {docs.overview.title}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div 
                  className="prose prose-purple max-w-none"
                  dangerouslySetInnerHTML={{ __html: renderMarkdownContent(docs.overview.content) }}
                  data-testid="overview-content"
                />
              </CardContent>
            </Card>
          </TabsContent>

          {/* Quick Starts Tab */}
          <TabsContent value="quickstarts" className="space-y-4" data-testid="tab-content-quickstarts">
            <Card className="bg-white/70 backdrop-blur-sm border-0 shadow-lg">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 bg-gradient-to-r from-purple-600 to-teal-600 bg-clip-text text-transparent">
                  <Zap className="h-5 w-5 text-purple-600" />
                  {docs.quickStarts.title}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div 
                  className="prose prose-purple max-w-none mb-6"
                  dangerouslySetInnerHTML={{ __html: renderMarkdownContent(docs.quickStarts.content) }}
                  data-testid="quickstarts-content"
                />
                
                {docs.quickStarts.subsections && (
                  <div className="space-y-4">
                    {docs.quickStarts.subsections.map((subsection, index) => (
                      <Card key={subsection.id} className="border-purple-200" data-testid={`quickstart-section-${index}`}>
                        <CardHeader className="pb-3">
                          <CardTitle className="text-lg text-purple-600">
                            {subsection.title}
                          </CardTitle>
                        </CardHeader>
                        <CardContent>
                          <div 
                            className="prose prose-sm max-w-none"
                            dangerouslySetInnerHTML={{ __html: renderMarkdownContent(subsection.content) }}
                          />
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Features Tab */}
          <TabsContent value="features" className="space-y-4" data-testid="tab-content-features">
            <Card className="bg-white/70 backdrop-blur-sm border-0 shadow-lg">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 bg-gradient-to-r from-purple-600 to-teal-600 bg-clip-text text-transparent">
                  <Settings className="h-5 w-5 text-purple-600" />
                  {docs.features.title}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div 
                  className="prose prose-purple max-w-none mb-6"
                  dangerouslySetInnerHTML={{ __html: renderMarkdownContent(docs.features.content) }}
                  data-testid="features-content"
                />
                
                {docs.features.subsections && (
                  <div className="space-y-4">
                    {docs.features.subsections.map((subsection, index) => (
                      <Card key={subsection.id} className="border-teal-200" data-testid={`feature-section-${index}`}>
                        <CardHeader className="pb-3">
                          <CardTitle className="text-lg text-teal-600">
                            {subsection.title}
                          </CardTitle>
                        </CardHeader>
                        <CardContent>
                          <div 
                            className="prose prose-sm max-w-none"
                            dangerouslySetInnerHTML={{ __html: renderMarkdownContent(subsection.content) }}
                          />
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* FAQ Tab */}
          <TabsContent value="faq" className="space-y-4" data-testid="tab-content-faq">
            <Card className="bg-white/70 backdrop-blur-sm border-0 shadow-lg">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 bg-gradient-to-r from-purple-600 to-teal-600 bg-clip-text text-transparent">
                  <HelpCircle className="h-5 w-5 text-purple-600" />
                  {docs.faq.title}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div 
                  className="prose prose-purple max-w-none mb-6"
                  dangerouslySetInnerHTML={{ __html: renderMarkdownContent(docs.faq.content) }}
                  data-testid="faq-content"
                />
                
                {docs.faq.subsections && (
                  <div className="space-y-4">
                    {docs.faq.subsections.map((subsection, index) => (
                      <Card key={subsection.id} className="border-amber-200" data-testid={`faq-section-${index}`}>
                        <CardHeader className="pb-3">
                          <CardTitle className="text-lg text-amber-600">
                            {subsection.title}
                          </CardTitle>
                        </CardHeader>
                        <CardContent>
                          <div 
                            className="prose prose-sm max-w-none"
                            dangerouslySetInnerHTML={{ __html: renderMarkdownContent(subsection.content) }}
                          />
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Support Tab */}
          <TabsContent value="support" className="space-y-4" data-testid="tab-content-support">
            <Card className="bg-white/70 backdrop-blur-sm border-0 shadow-lg">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 bg-gradient-to-r from-purple-600 to-teal-600 bg-clip-text text-transparent">
                  <MessageSquare className="h-5 w-5 text-purple-600" />
                  {docs.support.title}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div 
                  className="prose prose-purple max-w-none mb-6"
                  dangerouslySetInnerHTML={{ __html: renderMarkdownContent(docs.support.content) }}
                  data-testid="support-content"
                />
                
                {docs.support.subsections && (
                  <div className="space-y-4">
                    {docs.support.subsections.map((subsection, index) => (
                      <Card key={subsection.id} className="border-green-200" data-testid={`support-section-${index}`}>
                        <CardHeader className="pb-3">
                          <CardTitle className="text-lg text-green-600">
                            {subsection.title}
                          </CardTitle>
                        </CardHeader>
                        <CardContent>
                          <div 
                            className="prose prose-sm max-w-none"
                            dangerouslySetInnerHTML={{ __html: renderMarkdownContent(subsection.content) }}
                          />
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Quick Actions */}
        <Card className="bg-gradient-to-r from-purple-50 to-teal-50 border-purple-200">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-purple-600">
              <CheckCircle className="h-5 w-5" />
              Quick Actions
            </CardTitle>
            <CardDescription>
              Common administrative tasks and shortcuts
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Button 
                variant="outline" 
                className="justify-start" 
                onClick={() => setActiveTab("quickstarts")}
                data-testid="button-view-quickstarts"
              >
                <Zap className="h-4 w-4 mr-2" />
                View Quick Starts
              </Button>
              <Button 
                variant="outline" 
                className="justify-start"
                onClick={() => setActiveTab("faq")}
                data-testid="button-view-faq"
              >
                <HelpCircle className="h-4 w-4 mr-2" />
                Browse FAQ
              </Button>
              <Button 
                variant="outline" 
                className="justify-start"
                onClick={() => emailDocsMutation.mutate()}
                disabled={emailDocsMutation.isPending}
                data-testid="button-email-docs-quick"
              >
                <Mail className="h-4 w-4 mr-2" />
                Email Full Docs
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}